/**
 * PartagePage.jsx — Page publique de consultation d'un bilan partagé via QR.
 * URL : /partage/:token   (pas d'auth requise)
 * Affiche : bilan + prochains cours + bouton "Ajouter à mon planning" si connecté.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, User, ClipboardCheck, ArrowRight, AlertCircle, LogIn } from 'lucide-react';
import api from '../api';
import { useApp } from '../contexts/AppContext';
import { toast } from 'sonner';

export default function PartagePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useApp() || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);

  useEffect(() => {
    document.title = 'Bilan partagé · Code 2 Roues';
    (async () => {
      try {
        const r = await api.persoGetSharedByToken(token);
        setData(r);
      } catch (e) {
        setError(e.status === 410 ? 'Ce partage a expiré' : (e.message || 'Partage introuvable'));
      } finally { setLoading(false); }
    })();
  }, [token]);

  const importInMyPlanning = async () => {
    if (!user) { navigate('/login', { state: { from: `/partage/${token}` } }); return; }
    setImporting(true);
    try {
      await api.persoAcceptShared(token);
      setImported(true);
      toast.success('Ajouté à ton planning');
    } catch (e) { toast.error(e.message || 'Erreur'); }
    finally { setImporting(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] py-8 px-4" data-testid="partage-page">
      <div className="max-w-lg mx-auto">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-rose-500 mb-2 text-center">Bilan de fin de cours</div>
        <h1 className="text-3xl sm:text-4xl font-black text-white text-center mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {loading ? 'Chargement…' : (data?.title || 'Bilan partagé')}
        </h1>

        {loading && <div className="text-white/40 text-sm text-center">…</div>}

        {error && (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5 flex items-start gap-3" data-testid="partage-error">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-red-300 font-semibold">{error}</div>
              <div className="text-white/50 text-xs mt-1">Les partages expirent après 7 jours. Demande à ton moniteur de ré-envoyer un nouveau lien.</div>
            </div>
          </div>
        )}

        {data && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-rose-500/25 bg-rose-500/[0.05] p-5" data-testid="partage-meta">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-white/80"><Calendar size={14} className="text-rose-400" /> {new Date(data.date + 'T' + (data.start_time || '00:00')).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
                {data.start_time && <div className="flex items-center gap-2 text-white/80"><Clock size={14} className="text-rose-400" /> {data.start_time.slice(0, 5)} · {data.duration_min} min</div>}
                {data.contact_name && <div className="flex items-center gap-2 text-white/80"><User size={14} className="text-rose-400" /> {data.contact_name}</div>}
                {data.location && <div className="flex items-center gap-2 text-white/80"><MapPin size={14} className="text-rose-400" /> {data.location}</div>}
              </div>
            </div>

            {data.bilan_text && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-2 flex items-center gap-1.5"><ClipboardCheck size={11} /> Bilan de la séance</div>
                <div className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap" data-testid="partage-bilan">{data.bilan_text}</div>
              </div>
            )}

            {data.next_topics && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                <div className="text-[10px] font-black uppercase tracking-widest text-amber-400 mb-2 flex items-center gap-1.5"><ArrowRight size={11} /> Prochains cours à voir</div>
                <div className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">{data.next_topics}</div>
              </div>
            )}

            <div className="pt-2">
              {imported ? (
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-4 text-emerald-200 text-sm text-center">
                  ✓ Ajouté à ton planning — <Link to="/eleve/planning" className="underline">Voir mon planning</Link>
                </div>
              ) : (
                <button onClick={importInMyPlanning} disabled={importing} className="w-full px-4 py-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-black text-sm shadow-lg shadow-rose-900/30 transition inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="btn-import-partage">
                  {user ? (<>Ajouter à mon planning <ArrowRight size={14} /></>) : (<><LogIn size={14} /> Me connecter pour l&apos;ajouter</>)}
                </button>
              )}
              <div className="text-[11px] text-white/40 text-center mt-3">
                Aucun compte ? <Link to="/register" className="underline text-white/60">Créer un compte gratuit</Link> pour garder l&apos;historique de tes cours.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
